import { apiError, apiFetch, apiUrl } from "./api";
import { DETACH_DELAY_MS, API_FETCH_TIMEOUT_MS, API_FETCH_LONG_TIMEOUT_MS } from "@cookies/shared/config";

export { apiBase, apiUrl, loginUrl } from "./api";

export interface SessionUser {
  id: string;
  username: string;
  avatar?: string | null;
}

export interface BakeryGuild {
  id: string;
  name: string;
  icon?: string | null;
  memberCount?: number;
  bakeryLevel?: number;
  botPresent?: boolean;
  embeddable?: boolean;
  joinable?: boolean;
  installUrl?: string | null;
  botReason?: string | null;
  embedReason?: string | null;
  canManage?: boolean;
  permissions?: string;
}

export interface Session {
  token: string;
  user: SessionUser;
}

const TOKEN_KEY = "cb_token";
const USER_KEY = "cb_user";

function readLS(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLS(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    return;
  }
}

export { isDiscordFrame, isAppChannelFrame } from "../utils/frame";

export function getToken(): string | null {
  return readLS(TOKEN_KEY);
}

export function getStoredUser(): SessionUser | null {
  const raw = readLS(USER_KEY);
  if (!raw) return null;
  try {
    const u = JSON.parse(raw) as Partial<SessionUser>;
    if (typeof u.id === "string" && typeof u.username === "string") {
      return { id: u.id, username: u.username, avatar: u.avatar ?? null };
    }
  } catch {
    return null;
  }
  return null;
}

export function setSession(token: string, user: SessionUser): void {
  writeLS(TOKEN_KEY, token);
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    return;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    return;
  }
}

export function resolveGuildId(): string | null {
  try {
    const path = window.location.pathname;
    const m = path.match(/^\/bakery\/([^/]+)/);
    if (m?.[1]) return decodeURIComponent(m[1]);
    const q = new URLSearchParams(window.location.search);
    const proxied = q.get("guild_id") ?? q.get("guildId");
    if (proxied) return proxied;
  } catch {
    return null;
  }
  return null;
}

type RawUser = Partial<SessionUser> & { user_id?: string; name?: string };

function normalizeUser(u: RawUser): SessionUser | null {
  const id = u.id ?? u.user_id ?? "";
  const username = u.username ?? u.name ?? "";
  if (!id || !username) return null;
  return { id, username, avatar: u.avatar ?? null };
}

const pendingExchanges = new Map<string, Promise<Session>>();

export async function exchangeCode(code: string, signal?: AbortSignal): Promise<Session> {
  const pending = pendingExchanges.get(code);
  if (pending) return pending;
  const task = (async (): Promise<Session> => {
    const data = await apiFetch<{ token?: string; user?: RawUser }>("/api/auth/callback", {
      method: "POST",
      body: { code },
      signal,
    });
    if (!data.token || !data.user) throw new Error("auth callback: bad response");
    const user = normalizeUser(data.user);
    if (!user) throw new Error("auth callback: bad response");
    return { token: data.token, user };
  })();
  pendingExchanges.set(code, task);
  try {
    return await task;
  } finally {
    if (pendingExchanges.get(code) === task) pendingExchanges.delete(code);
  }
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

function meKey(explicitToken?: string | null): string {
  if (explicitToken !== undefined) return "me:" + String(explicitToken);
  return "me:stored:" + String(readLS(TOKEN_KEY));
}

export type MeHandlers = {
  signal?: AbortSignal;
};

type MeEntry = {
  ctrl: AbortController;
  promise: Promise<SessionUser>;
  handlers: Set<MeHandlers>;
  timer: ReturnType<typeof setTimeout> | undefined;
};

const inflightMe = new Map<string, MeEntry>();

export type GuildsHandlers = {
  signal?: AbortSignal;
};

type GuildsEntry = {
  ctrl: AbortController;
  promise: Promise<BakeryGuild[]>;
  handlers: Set<GuildsHandlers>;
  timer: ReturnType<typeof setTimeout> | undefined;
};

const inflightGuilds = new Map<string, GuildsEntry>();

function guildsKey(token: string): string {
  return "guilds:" + token;
}

export function detachMe(explicitToken: string | null | undefined, handlers: MeHandlers): void {
  const key = meKey(explicitToken);
  const entry = inflightMe.get(key);
  if (!entry) return;
  entry.handlers.delete(handlers);
  if (entry.handlers.size !== 0) return;
  if (entry.timer !== undefined) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    const cur = inflightMe.get(key);
    if (!cur || cur !== entry) return;
    if (cur.handlers.size !== 0) {
      cur.timer = undefined;
      return;
    }
    try {
      cur.ctrl.abort();
    } catch {}
    inflightMe.delete(key);
  }, DETACH_DELAY_MS);
}

export function detachGuilds(token: string, handlers: GuildsHandlers): void {
  const key = guildsKey(token);
  const entry = inflightGuilds.get(key);
  if (!entry) return;
  entry.handlers.delete(handlers);
  if (entry.handlers.size !== 0) return;
  if (entry.timer !== undefined) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    const cur = inflightGuilds.get(key);
    if (!cur || cur !== entry) return;
    if (cur.handlers.size !== 0) {
      cur.timer = undefined;
      return;
    }
    try {
      cur.ctrl.abort();
    } catch {}
    inflightGuilds.delete(key);
  }, DETACH_DELAY_MS);
}

type GuildStreamGuilds = { type: "guilds"; guilds: BakeryGuild[] };
type GuildStreamPresence = { type: "presence"; guild: BakeryGuild };
type GuildStreamDone = { type: "done" };
type GuildStreamEvent = GuildStreamGuilds | GuildStreamPresence | GuildStreamDone;

export type GuildStreamHandlers = {
  onGuilds?: (guilds: BakeryGuild[]) => void;
  onPresence?: (guild: BakeryGuild) => void;
  onDone?: () => void;
  signal?: AbortSignal;
};

type StreamEntry = {
  ctrl: AbortController;
  promise: Promise<void>;
  handlers: Set<GuildStreamHandlers>;
  timer: ReturnType<typeof setTimeout> | undefined;
  reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
};

const inflightStream = new Map<string, StreamEntry>();

function streamKey(token: string): string {
  return "stream:" + token;
}

// Drop all deduped auth/guild inflight entries so a manual retry issues
// fresh requests instead of rejoining a stale pending attempt. Entries are
// removed without aborting: aborting would trigger the single-retry path
// inside fetchMe/fetchGuilds and re-issue the request. Stale tasks finish
// in the background and are ignored by callers (run-id guards).
export function resetAuthInflight(): void {
  for (const entry of inflightMe.values()) {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
  }
  inflightMe.clear();
  for (const entry of inflightGuilds.values()) {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
  }
  inflightGuilds.clear();
  for (const entry of inflightStream.values()) {
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    try {
      entry.reader?.cancel()?.catch(() => undefined);
    } catch {}
  }
  inflightStream.clear();
}

export function detachGuildsStream(token: string, handlers: GuildStreamHandlers): void {
  const key = streamKey(token);
  const entry = inflightStream.get(key);
  if (!entry) return;
  entry.handlers.delete(handlers);
  if (entry.handlers.size !== 0) return;
  if (entry.timer !== undefined) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    const cur = inflightStream.get(key);
    if (!cur || cur !== entry) return;
    if (cur.handlers.size !== 0) {
      cur.timer = undefined;
      return;
    }
    try {
      cur.ctrl.abort();
    } catch {}
    try {
      void cur.reader?.cancel();
    } catch {}
    inflightStream.delete(key);
  }, DETACH_DELAY_MS);
}

async function requestMe(explicitToken?: string | null, signal?: AbortSignal): Promise<SessionUser> {
  const data = await apiFetch<RawUser & { user?: RawUser }>("/api/auth/me", {
    ...(explicitToken !== undefined ? { token: explicitToken } : {}),
    timeoutMs: API_FETCH_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
  const raw: RawUser | undefined =
    data && typeof data === "object" && "user" in data && data.user && typeof data.user === "object"
      ? (data.user as RawUser)
      : (data as RawUser);
  const user = normalizeUser(raw ?? {});
  if (!user) throw new Error("auth me: bad response");
  return user;
}

async function requestGuilds(token: string, signal?: AbortSignal, refresh = false): Promise<BakeryGuild[]> {
  const path = refresh ? "/api/guilds?refresh=1&presence=full" : "/api/guilds?presence=fast";
  const data = await apiFetch<{ guilds?: BakeryGuild[] } | BakeryGuild[]>(path, {
    token,
    timeoutMs: API_FETCH_LONG_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.guilds)) return data.guilds;
  throw new Error("guilds: bad response");
}

function asAbortSignal(v: unknown): AbortSignal | undefined {
  if (v && typeof v === "object" && typeof (v as AbortSignal).aborted === "boolean" && typeof (v as AbortSignal).addEventListener === "function") {
    return v as AbortSignal;
  }
  return undefined;
}

export async function fetchMe(
  explicitToken?: string | null,
  a?: AbortSignal | MeHandlers,
  b?: MeHandlers
): Promise<SessionUser> {
  let handlers: MeHandlers | undefined;
  let signal: AbortSignal | undefined;
  const maybeSignal = asAbortSignal(a);
  if (maybeSignal) {
    signal = maybeSignal;
    handlers = b;
    if (handlers?.signal) signal = handlers.signal;
  } else {
    handlers = (a as MeHandlers | undefined) ?? b;
    signal = handlers?.signal;
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const key = meKey(explicitToken);
  const existing = inflightMe.get(key);
  if (existing) {
    if (handlers) existing.handlers.add(handlers);
    if (existing.timer !== undefined) {
      clearTimeout(existing.timer);
      existing.timer = undefined;
    }
    return existing.promise;
  }
  const ctrl = new AbortController();
  const handlersSet = new Set<MeHandlers>();
  if (handlers) handlersSet.add(handlers);
  const entry: MeEntry = {
    ctrl,
    promise: Promise.resolve() as unknown as Promise<SessionUser>,
    handlers: handlersSet,
    timer: undefined,
  };
  let retried = false;
  const task = (async (): Promise<SessionUser> => {
    try {
      return await requestMe(explicitToken, ctrl.signal);
    } catch (e) {
      if (isAbortError(e) && !retried && !signal?.aborted) {
        retried = true;
        const nextCtrl = new AbortController();
        entry.ctrl = nextCtrl;
        return await requestMe(explicitToken, nextCtrl.signal);
      }
      throw e;
    }
  })();
  entry.promise = task;
  inflightMe.set(key, entry);
  try {
    return await task;
  } finally {
    const cur = inflightMe.get(key);
    if (cur && cur.promise === task) {
      if (cur.timer !== undefined) clearTimeout(cur.timer);
      inflightMe.delete(key);
    }
  }
}

export async function fetchGuilds(
  token: string,
  a?: AbortSignal | GuildsHandlers,
  b?: GuildsHandlers,
  refresh = false
): Promise<BakeryGuild[]> {
  let handlers: GuildsHandlers | undefined;
  let signal: AbortSignal | undefined;
  const maybeSignal = asAbortSignal(a);
  if (maybeSignal) {
    signal = maybeSignal;
    handlers = b;
    if (handlers?.signal) signal = handlers.signal;
  } else {
    handlers = (a as GuildsHandlers | undefined) ?? b;
    signal = handlers?.signal;
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (refresh) {
    return requestGuilds(token, signal, true);
  }
  const key = guildsKey(token);
  const existing = inflightGuilds.get(key);
  if (existing) {
    if (handlers) existing.handlers.add(handlers);
    if (existing.timer !== undefined) {
      clearTimeout(existing.timer);
      existing.timer = undefined;
    }
    return existing.promise;
  }
  const ctrl = new AbortController();
  const handlersSet = new Set<GuildsHandlers>();
  if (handlers) handlersSet.add(handlers);
  const entry: GuildsEntry = {
    ctrl,
    promise: Promise.resolve() as unknown as Promise<BakeryGuild[]>,
    handlers: handlersSet,
    timer: undefined,
  };
  let retried = false;
  const task = (async (): Promise<BakeryGuild[]> => {
    try {
      return await requestGuilds(token, ctrl.signal, false);
    } catch (e) {
      if (isAbortError(e) && !retried && !signal?.aborted) {
        retried = true;
        const nextCtrl = new AbortController();
        entry.ctrl = nextCtrl;
        return await requestGuilds(token, nextCtrl.signal, false);
      }
      throw e;
    }
  })();
  entry.promise = task;
  inflightGuilds.set(key, entry);
  try {
    return await task;
  } finally {
    const cur = inflightGuilds.get(key);
    if (cur && cur.promise === task) {
      if (cur.timer !== undefined) clearTimeout(cur.timer);
      inflightGuilds.delete(key);
    }
  }
}

async function runStream(token: string, ctrl: AbortController, entry: StreamEntry, refresh = false): Promise<void> {
  const path = refresh ? apiUrl("/api/guilds/stream?refresh=1") : apiUrl("/api/guilds/stream");
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    signal: ctrl.signal,
    headers: {
      ...(token ? { authorization: "Bearer " + token } : {}),
    },
  });
  if (!res.ok) {
    let msg = "request failed: " + res.status;
    try {
      const clone = res.clone();
      const j = (await clone.json()) as Record<string, unknown>;
      if (typeof j["error"] === "string" && j["error"]) msg = j["error"] as string;
      else if (typeof j["message"] === "string" && j["message"]) msg = j["message"] as string;
    } catch {}
    throw apiError(msg, res.status);
  }
  const body = res.body;
  if (!body) throw apiError("no body", 500);
  const reader = body.getReader();
  entry.reader = reader;
  const decoder = new TextDecoder();
  let buf = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        try {
          const evt = JSON.parse(t) as GuildStreamEvent;
          if (evt.type === "guilds" && Array.isArray((evt as GuildStreamGuilds).guilds)) {
            const gg = (evt as GuildStreamGuilds).guilds;
            for (const h of entry.handlers) {
              try {
                h.onGuilds?.(gg);
              } catch {}
            }
          } else if (evt.type === "presence" && (evt as GuildStreamPresence).guild) {
            const g = (evt as GuildStreamPresence).guild;
            for (const h of entry.handlers) {
              try {
                h.onPresence?.(g);
              } catch {}
            }
          } else if (evt.type === "done") {
            for (const h of entry.handlers) {
              try {
                h.onDone?.();
              } catch {}
            }
          }
        } catch {}
      }
    }
    const tail = buf.trim();
    if (tail) {
      try {
        const evt = JSON.parse(tail) as GuildStreamEvent;
        if (evt.type === "guilds" && Array.isArray((evt as GuildStreamGuilds).guilds)) {
          const gg = (evt as GuildStreamGuilds).guilds;
          for (const h of entry.handlers) {
            try {
              h.onGuilds?.(gg);
            } catch {}
          }
        } else if (evt.type === "presence" && (evt as GuildStreamPresence).guild) {
          const g = (evt as GuildStreamPresence).guild;
          for (const h of entry.handlers) {
            try {
              h.onPresence?.(g);
            } catch {}
          }
        } else if (evt.type === "done") {
          for (const h of entry.handlers) {
            try {
              h.onDone?.();
            } catch {}
          }
        }
      } catch {}
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {}
    entry.reader = undefined;
  }
}

export async function fetchGuildsStream(
  token: string,
  a?: AbortSignal | GuildStreamHandlers,
  b?: GuildStreamHandlers,
  refresh = false
): Promise<void> {
  let handlers: GuildStreamHandlers | undefined;
  let signal: AbortSignal | undefined;
  if (a && typeof a === "object" && "onGuilds" in a) {
    handlers = a as GuildStreamHandlers;
    signal = handlers.signal;
  } else {
    signal = a as AbortSignal | undefined;
    handlers = b;
    if (handlers && handlers.signal) signal = handlers.signal;
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (refresh) {
    const ctrl = new AbortController();
    const entry: StreamEntry = {
      ctrl,
      promise: Promise.resolve(),
      handlers: new Set<GuildStreamHandlers>(handlers ? [handlers] : []),
      timer: undefined,
      reader: undefined,
    };
    if (signal) {
      signal.addEventListener("abort", () => {
        try {
          ctrl.abort();
        } catch {}
      }, { once: true });
    }
    await runStream(token, ctrl, entry, true);
    return;
  }
  const key = streamKey(token);
  const existing = inflightStream.get(key);
  if (existing) {
    if (handlers) existing.handlers.add(handlers);
    if (existing.timer !== undefined) {
      clearTimeout(existing.timer);
      existing.timer = undefined;
    }
    return existing.promise;
  }
  const ctrl = new AbortController();
  const handlersSet = new Set<GuildStreamHandlers>();
  if (handlers) handlersSet.add(handlers);
  const entry: StreamEntry = {
    ctrl,
    promise: Promise.resolve(),
    handlers: handlersSet,
    timer: undefined,
    reader: undefined,
  };
  let retried = false;
  const task = (async (): Promise<void> => {
    try {
      await runStream(token, ctrl, entry, false);
    } catch (e) {
      if (isAbortError(e) && !retried && !signal?.aborted) {
        retried = true;
        const nextCtrl = new AbortController();
        entry.ctrl = nextCtrl;
        await runStream(token, nextCtrl, entry, false);
        return;
      }
      throw e;
    }
  })();
  entry.promise = task;
  inflightStream.set(key, entry);
  try {
    return await task;
  } finally {
    const cur = inflightStream.get(key);
    if (cur && cur.promise === task) {
      if (cur.timer !== undefined) clearTimeout(cur.timer);
      inflightStream.delete(key);
    }
  }
}
